
(() => {
  const CONFIG = {
    enabled: window.enzyBogoConfig?.enabled === true,
    debug: true,
    formId: 'R3yj2x',
    collectionHandle: 'shop-buy-one-get-one-50-offers',
    excludeSubscriptions: true,
    pageSize: 250,
    maxPages: 20,
    maxReminders: 3,
    declineText: 'No Thanks, Continue to Checkout',
    checkTimeout: 6000,
    popupTimeout: 3000,
    closeDelay: 1000
  };

  const root = window.Shopify?.routes?.root || '/';
  const checkoutUrl = '/checkout';

  const sessionCountKey = 'enzy-bogo-reminder-count';
  const sessionDeclinedKey = 'enzy-bogo-reminder-declined';

  let pending = false;
  let popupOpened = false;
  let redirectingToCollection = false;
  let popupTimer = null;
  let closeTimer = null;
  let checkTimer = null;
  let countedThisAttempt = false;
  let declineRecordedThisAttempt = false;

  function log(...args) {
    if (CONFIG.debug) {
      console.log('[BOGO PROD]', ...args);
    }
  }

  function readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      log('Session storage unavailable');
    }
  }

  function getSessionCount() {
    const count = Number(readSession(sessionCountKey));
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  function hasDeclined() {
    return readSession(sessionDeclinedKey) === 'true';
  }

  function recordPopupOpen() {
    if (countedThisAttempt) return;

    countedThisAttempt = true;
    const count = getSessionCount() + 1;

    writeSession(sessionCountKey, String(count));
    log(`Reminder appearance ${count} of ${CONFIG.maxReminders}`);
  }

  function recordDecline() {
    if (declineRecordedThisAttempt) return;

    declineRecordedThisAttempt = true;
    writeSession(sessionDeclinedKey, 'true');
    log('No Thanks selected - reminders disabled for session');
  }

  async function fetchJSON(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  async function getEligibleIds() {
    const ids = new Set();

    for (let page = 1; page <= CONFIG.maxPages; page++) {
      const data = await fetchJSON(
        `${root}collections/${CONFIG.collectionHandle}/products.json?limit=${CONFIG.pageSize}&page=${page}`
      );

      if (!Array.isArray(data.products)) {
        throw new Error('Invalid BOGO collection response');
      }

      data.products.forEach(product => {
        ids.add(String(product.id));
      });

      if (data.products.length < CONFIG.pageSize) {
        if (ids.size === 0) {
          throw new Error('BOGO collection returned no products');
        }

        return ids;
      }
    }

    throw new Error('Incomplete BOGO collection lookup');
  }

  async function checkEligibility() {
    const [cart, eligibleIds] = await Promise.all([
      fetchJSON(`${root}cart.js`),
      getEligibleIds()
    ]);

    if (!Array.isArray(cart.items)) {
      throw new Error('Invalid Shopify cart response');
    }

    const eligibleQuantity = cart.items.reduce((total, item) => {
      const eligible =
        eligibleIds.has(String(item.product_id)) &&
        (
          !CONFIG.excludeSubscriptions ||
          !item.selling_plan_allocation
        );

      return eligible
        ? total + Number(item.quantity)
        : total;
    }, 0);

    const shouldShow =
      eligibleQuantity > 0 &&
      eligibleQuantity % 2 === 1;

    log('Collection products:', eligibleIds.size);
    log('Total cart units:', cart.item_count);
    log('Eligible BOGO units:', eligibleQuantity);
    log('Should show reminder:', shouldShow);

    return shouldShow;
  }

  function clearTimers() {
    clearTimeout(popupTimer);
    clearTimeout(closeTimer);
    clearTimeout(checkTimer);
  }

  function continueCheckout() {
    if (!pending || redirectingToCollection) return;

    log('Continuing to Shopify checkout');

    pending = false;
    clearTimers();
    window.location.assign(checkoutUrl);
  }

  function openPopup() {
    log('Requesting Klaviyo popup');

    popupOpened = false;

    popupTimer = setTimeout(() => {
      if (pending && !popupOpened) {
        log('Popup timeout - continuing checkout');
        continueCheckout();
      }
    }, CONFIG.popupTimeout);

    try {
      if (!window._klOnsite) {
        log('Klaviyo unavailable');
        continueCheckout();
        return;
      }

      window._klOnsite.push(['openForm', CONFIG.formId]);
    } catch (error) {
      log('Klaviyo error:', error);
      continueCheckout();
    }
  }

  // Detect "No Thanks" separately from the X button.
  // Listen in the capture phase before Klaviyo closes the form.
  function detectDeclineClick(event) {
    if (!pending || !popupOpened) return;

    const path = typeof event.composedPath === 'function'
      ? event.composedPath()
      : [event.target];

    const expected = CONFIG.declineText
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    for (const element of path) {
      if (!element || typeof element.matches !== 'function') {
        continue;
      }

      if (!element.matches('button, a, [role="button"]')) {
        continue;
      }

      const text = (element.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      if (text === expected) {
        recordDecline();
        return;
      }
    }
  }

  document.addEventListener('pointerdown', detectDeclineClick, true);
  document.addEventListener('click', detectDeclineClick, true);

  window.addEventListener('klaviyoForms', event => {
    const detail = event.detail || {};

    if (
      String(detail.formId) !== CONFIG.formId ||
      !pending
    ) {
      return;
    }

    log('Klaviyo event:', detail.type);

    if (detail.type === 'open' && !popupOpened) {
      popupOpened = true;
      clearTimeout(popupTimer);
      recordPopupOpen();
    }

    if (
      detail.type === 'redirectedToUrl' ||
      detail.type === 'redirectedToUrlFromStep'
    ) {
      redirectingToCollection = true;
      pending = false;
      clearTimers();

      log('Shopping redirect - checkout cancelled');
      return;
    }

    if (
      detail.type === 'close' &&
      !redirectingToCollection
    ) {
      clearTimeout(closeTimer);

      closeTimer = setTimeout(() => {
        if (!redirectingToCollection) {
          continueCheckout();
        }
      }, CONFIG.closeDelay);
    }
  });

  async function handleCheckout() {
    pending = true;
    popupOpened = false;
    redirectingToCollection = false;
    countedThisAttempt = false;
    declineRecordedThisAttempt = false;

    log('Checkout intercepted');

    if (hasDeclined()) {
      log('Customer previously selected No Thanks');
      continueCheckout();
      return;
    }

    if (getSessionCount() >= CONFIG.maxReminders) {
      log('Three-reminder limit reached');
      continueCheckout();
      return;
    }

    checkTimer = setTimeout(() => {
      if (pending && !popupOpened) {
        log('Eligibility timeout - allowing checkout');
        continueCheckout();
      }
    }, CONFIG.checkTimeout);

    try {
      const shouldShow = await checkEligibility();

      if (!pending) return;

      clearTimeout(checkTimer);

      if (shouldShow) {
        openPopup();
      } else {
        continueCheckout();
      }
    } catch (error) {
      log('Eligibility failed:', error);
      continueCheckout();
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    const button = event.submitter;

    if (
      !['CartDrawer-Form', 'cart'].includes(form.id) ||
      button?.name !== 'checkout'
    ) {
      return;
    }

    if (!CONFIG.enabled) return;

    event.preventDefault();

    if (pending) {
      log('Duplicate checkout ignored');
      return;
    }

    void handleCheckout();
  }, true);

  log(
    'Build 3E loaded',
    'Reminder enabled:',
    CONFIG.enabled,
    'Appearances:',
    getSessionCount(),
    'Declined:',
    hasDeclined()
  );
})();
